import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Button, message, Tooltip, Empty, Radio, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import { PlusCircleOutlined } from '@ant-design/icons';
import api from '@services/api';
import SessionContext from '@providers/Session/SessionContext';
import useSearchParams from '@hooks/useSearchParams';
import rolesConstants from '@utils/rolesConstants';
import CustomCard from '@components/CustomCard';
import SelectCustomers from '@components/SelectCustomers';
import { IGroupsData, IGroup } from '../types';
import GroupViewerModal from './GroupViewerModal';

import * as S from './styles';
import GroupTag from './GroupTag';

const UserGroups = () => {
  const { t } = useTranslation(['roles', 'attributes']);

  const {
    isRoot,
    mainCustomerInContext,
    effectiveRoles,
    userLogged,
    hasOnlyMotBusiness,
    isParentCustomer,
    customer: { customersData },
  } = useContext(SessionContext);

  const customerIdLogged = userLogged?.customerId;
  const isCustomerParent = customerIdLogged === mainCustomerInContext?.id;
  const rolesToAccess = effectiveRoles?.portal;
  const isCustomerCanManage = rolesToAccess?.includes(
    rolesConstants.MANAGE_GROUPS,
  );
  const isSuperBroker = userLogged?.customerType === 'SUPERBROKER';
  const showRadio = isParentCustomer && (isRoot || !isSuperBroker);

  const [modalViewerVisible, setModalViewerVisible] = useState(false);
  const [groupId, setGroupId] = useState('');
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>();

  const { setSearchParams, searchParams } = useSearchParams({
    type: 'pagination',
    defaultParams: ['activeGroup', 'customerId'],
  });

  const { activeGroup, customerId, page } = useMemo(
    () => searchParams,
    [searchParams],
  );

  const groupType = useMemo(
    () => (activeGroup === 'customers-groups' ? 'CUSTOMERS' : 'MINE'),
    [activeGroup],
  );

  const { data, mutate } = useSwr<IGroupsData>('/groups/', {
    params: {
      filter: groupType === 'MINE' ? 'MINE' : undefined,
      customerIds: customerId,
      page: page ? Number(page) - 1 : 0,
      linesPerPage: 500,
    },
  });

  const groupsData = useMemo(() => {
    if (!data || data?.content?.length === 0) {
      return undefined;
    }

    const groupsByCustomer = data?.content?.find(
      group => group?.attributes?.customerId === Number(customerId),
    )?.subGroups;

    return groupType === 'CUSTOMERS' ? groupsByCustomer : data?.content;
  }, [customerId, data, groupType]);

  const deleteGroup = useCallback(
    (id: string) => api.delete(`/groups/${id}`),
    [],
  );

  const handleMutateGroups = useCallback(
    (type: 'CREATE' | 'DELETE', newGroup: IGroup) => {
      if (groupsData) {
        if (type === 'CREATE') {
          mutate({
            ...data,
            content: [...groupsData, newGroup],
          });
        } else {
          mutate({
            ...data,
            content:
              groupsData?.filter(group => group.id !== newGroup.id) || [],
          });
        }
      }
    },
    [data, groupsData, mutate],
  );

  const handleActions = useCallback(
    async (actionType: 'UPDATE' | 'DELETE', userId: string) => {
      const isUpdateUser = actionType === 'UPDATE';

      if (isUpdateUser) {
        setGroupId(userId);
        setModalViewerVisible(true);
      } else {
        try {
          await deleteGroup(userId);
          handleMutateGroups('DELETE', { id: userId } as IGroup);
          message.success(t('msgs.success-deleting-profile'));
        } catch (e: any) {
          if (
            e?.response?.data?.code &&
            e?.response?.data?.code === 'GROUP_CANNOT_BE_DELETED'
          ) {
            message.error(t('msgs.error-deleting-profile-exists'));
          } else {
            message.error(t('msgs.error-deleting-profile'));
          }
        }
      }
    },
    [deleteGroup, t, handleMutateGroups],
  );

  const onClose = useCallback(() => {
    setModalViewerVisible(false);
    setGroupId('');
  }, []);

  return (
    <S.GroupsContainer>
      <GroupViewerModal
        type={groupType}
        groupId={groupId}
        visible={modalViewerVisible}
        onClose={onClose}
        mutateGroups={handleMutateGroups}
        selectedCustomerId={customerId}
        selectedCustomerName={selectedCustomerName}
      />
      <Row
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        {showRadio && (
          <Radio.Group
            onChange={({ target: { value } }) => {
              setSelectedCustomerName(undefined);
              setSearchParams({ activeGroup: value, customerId: undefined });
            }}
            defaultValue="my-groups"
            style={{
              marginBottom: 16,
            }}
          >
            <Radio.Button value="my-groups">{t('tabs.my-groups')}</Radio.Button>
            <Radio.Button value="customers-groups">
              {t(
                `tabs.${
                  hasOnlyMotBusiness ? 'department' : 'customers'
                }-groups`,
              )}
            </Radio.Button>
          </Radio.Group>
        )}

        <div style={{ display: 'flex', marginBottom: 16 }}>
          <div style={{ marginRight: 8 }}>
            {groupType === 'CUSTOMERS' && (
              <SelectCustomers
                value={customerId && Number(customerId)}
                onChange={value => {
                  const customerName = customersData?.find(
                    cst => cst.id === value,
                  )?.name;
                  setSelectedCustomerName(customerName);
                  setSearchParams({ customerId: value });
                }}
                style={{
                  width: 240,
                }}
                placeholder={t(
                  `selectA${hasOnlyMotBusiness ? 'Department' : 'Customer'}`,
                )}
              />
            )}
          </div>
          {isCustomerCanManage && (
            <Tooltip
              title={
                groupType === 'CUSTOMERS' &&
                !customerId &&
                t(
                  `msgs.selectA${
                    hasOnlyMotBusiness ? 'Department' : 'Customer'
                  }`,
                )
              }
            >
              <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={() => setModalViewerVisible(true)}
                disabled={groupType === 'CUSTOMERS' && !customerId}
              >
                {t('actions.newRole')}
              </Button>
            </Tooltip>
          )}
        </div>
      </Row>
      <CustomCard loading={!data}>
        {groupsData ? (
          groupsData.map(group => (
            <GroupTag
              group={{
                id: group?.id,
                name: group?.name || group.attributes?.prettyName,
                usernames: group.usernames,
              }}
              handleActions={handleActions}
              notShowAdmin={
                group.name === 'ADMINISTRATORS' &&
                isCustomerParent &&
                groupType === 'MINE'
              }
            />
          ))
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              !selectedCustomerName
                ? t(
                    `no${
                      hasOnlyMotBusiness ? 'Department' : 'Customer'
                    }Informed`,
                  )
                : t('thereIsNoData')
            }
          />
        )}
      </CustomCard>
    </S.GroupsContainer>
  );
};

export default UserGroups;
